import { useState, useEffect, useCallback } from "react";
import { Character } from "@/mocks/characters";
import { useResolvedImage } from "@/hooks/useResolvedImage";
import { supabase } from "@/lib/supabase";

const PC_RESTRICTION_KEY = "charamap_pc_restriction";

const CharacterAvatar = ({ imageRef, name, className }: { imageRef: string; name: string; className?: string }) => {
  const src = useResolvedImage(imageRef);
  return (
    <img
      src={src || "https://public.readdy.ai/ai/img_res/56e8f00d84a330496e43401a9dd67dea.jpg"}
      alt={name}
      className={className ?? "w-full h-full object-cover object-top"}
    />
  );
};

interface Props {
  characters: Character[];
}

// ホーム表示設定の型
interface HomeDisplaySettings {
  count: number;       // 表示人数 (4〜16)
  orderBy: "sort_order" | "age_asc" | "age_desc" | "random"; // 表示順
  pinnedIds: string[]; // 固定表示するキャラID（先頭に表示）
}

const DEFAULT_HOME_SETTINGS: HomeDisplaySettings = {
  count: 8,
  orderBy: "sort_order",
  pinnedIds: [],
};

const ORDER_OPTIONS: { value: HomeDisplaySettings["orderBy"]; label: string; icon: string }[] = [
  { value: "sort_order", label: "登録順", icon: "ri-list-ordered" },
  { value: "age_asc", label: "年齢が若い順", icon: "ri-sort-asc" },
  { value: "age_desc", label: "年齢が高い順", icon: "ri-sort-desc" },
  { value: "random", label: "ランダム", icon: "ri-shuffle-line" },
];

const AdminSettings = ({ characters }: Props) => {
  const [pcRestriction, setPcRestriction] = useState(false);
  const [featuredCharIds, setFeaturedCharIds] = useState<string[]>([]);
  const [homeSettings, setHomeSettings] = useState<HomeDisplaySettings>(DEFAULT_HOME_SETTINGS);
  const [toast, setToast] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isHomeSaving, setIsHomeSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<"featured" | "home" | "pc">("featured");

  const loadSettings = useCallback(async () => {
    // Load PC restriction from Supabase (cloud) first, fallback to localStorage
    const { data: pcData } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", "pc_restriction")
      .maybeSingle();
    if (pcData?.value !== undefined) {
      const enabled = pcData.value === "true";
      setPcRestriction(enabled);
      localStorage.setItem(PC_RESTRICTION_KEY, String(enabled));
    } else {
      const val = localStorage.getItem(PC_RESTRICTION_KEY);
      setPcRestriction(val === "true");
    }

    // Load featured_char_ids
    const { data: featuredData } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", "featured_char_ids")
      .maybeSingle();

    if (featuredData?.value) {
      try {
        const ids = JSON.parse(featuredData.value);
        setFeaturedCharIds(Array.isArray(ids) ? ids : []);
      } catch {
        setFeaturedCharIds([]);
      }
    } else {
      // Migrate from old single featured_char_id
      const { data: oldData } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "featured_char_id")
        .maybeSingle();
      if (oldData?.value) {
        setFeaturedCharIds([oldData.value]);
      }
    }

    // Load home display settings
    const { data: homeData } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", "home_display_settings")
      .maybeSingle();

    if (homeData?.value) {
      try {
        const parsed = JSON.parse(homeData.value);
        setHomeSettings({ ...DEFAULT_HOME_SETTINGS, ...parsed });
      } catch {
        setHomeSettings(DEFAULT_HOME_SETTINGS);
      }
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleTogglePcRestriction = async () => {
    const next = !pcRestriction;
    setPcRestriction(next);
    localStorage.setItem(PC_RESTRICTION_KEY, String(next));

    // Save to Supabase so all devices reflect the change
    const { data: existing } = await supabase
      .from("site_settings")
      .select("key")
      .eq("key", "pc_restriction")
      .maybeSingle();

    if (existing) {
      await supabase
        .from("site_settings")
        .update({ value: String(next), updated_at: new Date().toISOString() })
        .eq("key", "pc_restriction");
    } else {
      await supabase
        .from("site_settings")
        .insert({ key: "pc_restriction", value: String(next), updated_at: new Date().toISOString() });
    }

    showToast(next ? "PC制限をONにしました（全端末に反映）" : "PC制限をOFFにしました（全端末に反映）");
  };

  // Save featured_char_ids to Supabase
  const saveFeaturedIds = async (ids: string[]) => {
    setIsSaving(true);
    try {
      // First check if key exists
      const { data: existing } = await supabase
        .from("site_settings")
        .select("key")
        .eq("key", "featured_char_ids")
        .maybeSingle();

      if (existing) {
        await supabase
          .from("site_settings")
          .update({ value: JSON.stringify(ids), updated_at: new Date().toISOString() })
          .eq("key", "featured_char_ids");
      } else {
        await supabase
          .from("site_settings")
          .insert({ key: "featured_char_ids", value: JSON.stringify(ids), updated_at: new Date().toISOString() });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleFeatured = async (id: string) => {
    let newIds: string[];
    if (featuredCharIds.includes(id)) {
      newIds = featuredCharIds.filter((x) => x !== id);
    } else {
      newIds = [...featuredCharIds, id];
    }
    setFeaturedCharIds(newIds);
    await saveFeaturedIds(newIds);
    showToast(
      newIds.includes(id)
        ? "おすすめに追加しました！（全端末に反映）"
        : "おすすめから外しました"
    );
  };

  const handleRemoveFeatured = async (id: string) => {
    const newIds = featuredCharIds.filter((x) => x !== id);
    setFeaturedCharIds(newIds);
    await saveFeaturedIds(newIds);
    showToast("おすすめから外しました");
  };

  // Save home display settings
  const saveHomeSettings = async (settings: HomeDisplaySettings) => {
    setIsHomeSaving(true);
    try {
      const { data: existing } = await supabase
        .from("site_settings")
        .select("key")
        .eq("key", "home_display_settings")
        .maybeSingle();

      if (existing) {
        await supabase
          .from("site_settings")
          .update({ value: JSON.stringify(settings), updated_at: new Date().toISOString() })
          .eq("key", "home_display_settings");
      } else {
        await supabase
          .from("site_settings")
          .insert({ key: "home_display_settings", value: JSON.stringify(settings), updated_at: new Date().toISOString() });
      }
      showToast("ホーム表示設定を保存しました！");
    } finally {
      setIsHomeSaving(false);
    }
  };

  const handleHomeCountChange = (count: number) => {
    setHomeSettings((prev) => ({ ...prev, count }));
  };

  const handleHomeOrderChange = (orderBy: HomeDisplaySettings["orderBy"]) => {
    setHomeSettings((prev) => ({ ...prev, orderBy }));
  };

  const handleTogglePinned = (id: string) => {
    setHomeSettings((prev) => {
      const newPinned = prev.pinnedIds.includes(id)
        ? prev.pinnedIds.filter((x) => x !== id)
        : [...prev.pinnedIds, id];
      return { ...prev, pinnedIds: newPinned };
    });
  };

  const handleSaveHomeSettings = () => {
    saveHomeSettings(homeSettings);
  };

  const activeChars = characters.filter((c) => c.isActive);
  const featuredChars = activeChars.filter((c) => featuredCharIds.includes(c.id));

  const SECTIONS = [
    { key: "featured", label: "★人気設定", icon: "ri-star-fill" },
    { key: "home", label: "ホーム表示", icon: "ri-home-4-line" },
    { key: "pc", label: "PC制限", icon: "ri-computer-line" },
  ] as const;

  return (
    <div className="flex flex-col gap-4">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-pink-500 text-white px-6 py-3 rounded-full text-sm font-medium animate-fade-in">
          <i className="ri-check-line mr-2"></i>{toast}
        </div>
      )}

      {/* Section tabs */}
      <div className="flex gap-2 bg-pink-50 p-1 rounded-2xl">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeSection === s.key
                ? "bg-white text-pink-500 shadow-sm"
                : "text-gray-500 hover:text-pink-400"
            }`}
          >
            <i className={s.icon}></i>
            {s.label}
          </button>
        ))}
      </div>

      {/* ===== FEATURED SECTION ===== */}
      {activeSection === "featured" && (
        <div className="bg-white rounded-2xl p-6 border border-pink-100">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 flex items-center justify-center bg-pink-50 rounded-full flex-shrink-0">
              <i className="ri-star-fill text-pink-400 text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-800">おすすめの女の子（複数設定可）</h3>
              <p className="text-sm text-gray-500">GPSマップでランダムに「★人気」として最も近い位置に表示されます</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-600 text-xs px-3 py-1 rounded-full">
              <i className="ri-cloud-line text-xs"></i>
              クラウド同期 — 全端末に即反映
            </div>
            <div className="flex items-center gap-1.5 bg-pink-50 border border-pink-200 text-pink-500 text-xs px-3 py-1 rounded-full">
              <i className="ri-shuffle-line text-xs"></i>
              毎回ランダムで1人が選ばれます
            </div>
            {isSaving && (
              <div className="flex items-center gap-1 text-xs text-pink-400">
                <i className="ri-loader-4-line animate-spin"></i>保存中...
              </div>
            )}
          </div>

          {/* Current featured list */}
          {featuredChars.length > 0 ? (
            <div className="flex flex-col gap-2 mb-4">
              <p className="text-xs font-semibold text-gray-500 mb-1">現在のおすすめ（{featuredChars.length}人）</p>
              {featuredChars.map((char) => (
                <div key={char.id} className="flex items-center gap-3 bg-pink-50 rounded-xl p-3 border border-pink-200">
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-pink-300 flex-shrink-0">
                    <CharacterAvatar imageRef={char.images[0]} name={char.name} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-800 text-sm">{char.name}</span>
                      <span className="text-pink-400 text-xs">{char.age != null ? `${char.age}歳` : "ひみつ"}</span>
                      <span className="text-xs bg-yellow-100 text-yellow-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <i className="ri-star-fill text-xs"></i>おすすめ中
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 truncate">{char.catchphrase}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveFeatured(char.id)}
                    className="w-7 h-7 flex items-center justify-center bg-white text-gray-400 rounded-full hover:text-red-400 hover:bg-red-50 transition-all cursor-pointer flex-shrink-0"
                    title="おすすめを解除"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl p-4 mb-4 border border-gray-200 text-center">
              <i className="ri-star-line text-gray-300 text-2xl mb-1 block"></i>
              <p className="text-sm text-gray-400">おすすめの女の子が設定されていません</p>
              <p className="text-xs text-gray-400 mt-0.5">下のリストから複数選択できます</p>
            </div>
          )}

          {/* Character selector */}
          <p className="text-xs font-semibold text-gray-500 mb-2">追加する（タップで選択）</p>
          <div className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
            {activeChars.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">表示中のキャラクターがいません</p>
            ) : (
              activeChars.map((char) => {
                const isSelected = featuredCharIds.includes(char.id);
                return (
                  <button
                    key={char.id}
                    type="button"
                    onClick={() => handleToggleFeatured(char.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer text-left ${
                      isSelected
                        ? "border-pink-400 bg-pink-50"
                        : "border-gray-200 bg-white hover:border-pink-200 hover:bg-pink-50/50"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-pink-200 flex-shrink-0">
                      <CharacterAvatar imageRef={char.images[0]} name={char.name} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-800 text-sm">{char.name}</span>
                        <span className="text-pink-400 text-xs">{char.age != null ? `${char.age}歳` : "ひみつ"}</span>
                        <span className="text-xs text-gray-400">{char.type}</span>
                      </div>
                      <p className="text-xs text-gray-400 truncate">{char.area}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? "border-pink-400 bg-pink-400" : "border-gray-300"
                    }`}>
                      {isSelected && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ===== HOME DISPLAY SECTION ===== */}
      {activeSection === "home" && (
        <div className="bg-white rounded-2xl p-6 border border-pink-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-pink-50 rounded-full flex-shrink-0">
              <i className="ri-home-4-line text-pink-400 text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-800">ホーム「人気の女の子」表示設定</h3>
              <p className="text-sm text-gray-500">トップページに表示するキャラの人数・順番を設定できます</p>
            </div>
          </div>

          {/* 表示人数 */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-3">
              表示人数：<span className="text-pink-500">{homeSettings.count}人</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {[4, 6, 8, 10, 12, 16].map((n) => (
                <button
                  key={n}
                  onClick={() => handleHomeCountChange(n)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    homeSettings.count === n
                      ? "bg-pink-500 text-white"
                      : "bg-pink-50 text-pink-500 hover:bg-pink-100"
                  }`}
                >
                  {n}人
                </button>
              ))}
            </div>
          </div>

          {/* 表示順 */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-3">表示順</p>
            <div className="grid grid-cols-2 gap-2">
              {ORDER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleHomeOrderChange(opt.value)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all cursor-pointer ${
                    homeSettings.orderBy === opt.value
                      ? "border-pink-400 bg-pink-50 text-pink-600"
                      : "border-gray-200 bg-white text-gray-600 hover:border-pink-200 hover:bg-pink-50/50"
                  }`}
                >
                  <i className={`${opt.icon} text-base`}></i>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 固定表示キャラ */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">
                先頭に固定表示するキャラ
                {homeSettings.pinnedIds.length > 0 && (
                  <span className="ml-2 text-xs bg-pink-100 text-pink-500 px-2 py-0.5 rounded-full">
                    {homeSettings.pinnedIds.length}人選択中
                  </span>
                )}
              </p>
              {homeSettings.pinnedIds.length > 0 && (
                <button
                  onClick={() => setHomeSettings((prev) => ({ ...prev, pinnedIds: [] }))}
                  className="text-xs text-gray-400 hover:text-red-400 cursor-pointer"
                >
                  全解除
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 mb-3">選択したキャラは表示順に関わらず先頭に表示されます</p>
            <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
              {activeChars.map((char) => {
                const isPinned = homeSettings.pinnedIds.includes(char.id);
                return (
                  <button
                    key={char.id}
                    type="button"
                    onClick={() => handleTogglePinned(char.id)}
                    className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all cursor-pointer text-left ${
                      isPinned
                        ? "border-pink-400 bg-pink-50"
                        : "border-gray-200 bg-white hover:border-pink-200 hover:bg-pink-50/50"
                    }`}
                  >
                    <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-pink-200 flex-shrink-0">
                      <CharacterAvatar imageRef={char.images[0]} name={char.name} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-gray-800 text-sm">{char.name}</span>
                        <span className="text-pink-400 text-xs">{char.age != null ? `${char.age}歳` : "ひみつ"}</span>
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isPinned ? "border-pink-400 bg-pink-400" : "border-gray-300"
                    }`}>
                      {isPinned && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Save button */}
          <button
            onClick={handleSaveHomeSettings}
            disabled={isHomeSaving}
            className="w-full py-3 bg-gradient-to-r from-pink-400 to-rose-400 text-white rounded-full font-bold text-sm hover:from-pink-500 hover:to-rose-500 transition-all cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isHomeSaving ? (
              <><i className="ri-loader-4-line animate-spin"></i>保存中...</>
            ) : (
              <><i className="ri-save-line"></i>ホーム表示設定を保存</>
            )}
          </button>
        </div>
      )}

      {/* ===== PC RESTRICTION SECTION ===== */}
      {activeSection === "pc" && (
        <div className="bg-white rounded-2xl p-6 border border-pink-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-pink-50 rounded-full flex-shrink-0 mt-0.5">
                <i className="ri-computer-line text-pink-400 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">PC制限モード</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  ONにすると、PCからのアクセス時に「スマートフォン専用」ページを表示します。<br />
                  管理画面（/admin）は常にPCからアクセス可能です。
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${pcRestriction ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${pcRestriction ? "bg-green-500" : "bg-gray-400"}`}></span>
                    {pcRestriction ? "PC制限 ON" : "PC制限 OFF"}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleTogglePcRestriction}
              className={`relative w-14 h-7 rounded-full transition-all cursor-pointer flex-shrink-0 mt-1 ${
                pcRestriction ? "bg-pink-400" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all shadow-sm ${
                  pcRestriction ? "left-8" : "left-1"
                }`}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
